import {defineStore} from "pinia";
import {useEnterpriseGatewayApiProxy} from "@/plugins/proxy-client-plugin.ts";
import {ref, toRefs} from "vue";
import {
    ActorDto,
    AthumiService,
    type IActorDto,
    type IInstantSubmitTopUpSession,
    InstantSubmitTopUpSession,
    type ITopUpSession,
} from "@/api/enterprise-gateway-api.proxy.ts";
import type {IBillingAddress} from "@/app/shared/components/aeg-address-form/aeg-address-form.ts";
import {getIsoCodeByName} from "@/app/shared/helpers/country.ts";
import {LoadingState} from "@/app/stores/loading-state.ts";
import {useUserStore} from "@/app/stores/user-store.ts";
import {IndexRoute} from "@/router/index-route.ts";
import {useErrorStore} from "@/app/stores/error-store.ts";

export interface ITopUpFormData {
    billingAddress: IBillingAddress;
    topUpAmount?: number;
    vatNumber?: string;
    companyName?: string;
    ownReference?: string;
}

export const useWalletStore = defineStore('wallet-store', () => {
    const apiProxy = useEnterpriseGatewayApiProxy();
    const userStore = useUserStore();
    const errorStore = useErrorStore();
    const {user} = toRefs(userStore);

    const tempBillingAddress = ref<IBillingAddress>();
    const loadingState = new LoadingState();

    const mapToTopUpRequest = (formData: ITopUpFormData, athumiService: AthumiService, originatingSessionId: string | undefined): InstantSubmitTopUpSession => {
        if (!formData.topUpAmount) {
            throw new Error('Top-up amount is required');
        }

        const {billingAddress, companyName, topUpAmount, vatNumber, ownReference} = formData;
        const {street, houseNumber, boxNumber, postalCode, city, country, emails, vatNumber: foreignOrOvoVatNumber} = billingAddress;

        const request: IInstantSubmitTopUpSession = {
            service: athumiService,
            callBackUrl: !originatingSessionId ? IndexRoute.name as string : undefined,
            transactionId: crypto.randomUUID(),
            originatingSessionId: originatingSessionId,
            subjectId: undefined,
            ownReference: ownReference,
            name: companyName,
            amount: parseFloat(topUpAmount?.toString().replace(',', '.')),
            street: street,
            streetNumber: houseNumber,
            bus: boxNumber,
            postCode: postalCode,
            city: city,
            countryRegionCode: getIsoCodeByName(country),
            emails: emails ? emails.map(e => e.email) : [],
            vatNumber: foreignOrOvoVatNumber || vatNumber,
            voOrgCode: user.value.voOrgCode
        };
        return request as InstantSubmitTopUpSession;
    };

    const setTempAddress = (billingAddress: IBillingAddress) => {
        tempBillingAddress.value = billingAddress;
    }

    const loadActor = async (athumiService: AthumiService): Promise<IActorDto | undefined> => {
        try {
            loadingState.startViewModelLoading();

            const actor = await apiProxy.actor_Get(
                athumiService,
                user.value.voOrgCode,
                ''
            );

            if (!actor.id) {
                if (tempBillingAddress.value) {
                    return updateActorAddress(tempBillingAddress.value, athumiService);
                }
                return actor;
            }

            return actor;
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error, true);
        } finally {
            loadingState.stopViewModelLoading();
        }
    };

    const updateActorAddress = (billingAddress: IBillingAddress, athumiService: AthumiService): ActorDto => {
        return new ActorDto({
            street: billingAddress.street,
            streetNumber: billingAddress.houseNumber,
            bus: billingAddress.boxNumber,
            city:  billingAddress.city,
            postCode: billingAddress.postalCode,
            countryCode:  getIsoCodeByName(billingAddress.country),
            email: billingAddress.emails.filter(e => e.service === athumiService).map(e => e.email)[0],
        });
    };

    const topUpWallet = async (athumiService: AthumiService, formData: ITopUpFormData, originatingSessionId: string | undefined): Promise<ITopUpSession | undefined> => {
        loadingState.startLoading();
        try {
            const request = mapToTopUpRequest(formData, athumiService, originatingSessionId);
            return await apiProxy.topUp_CreateAndSubmit(request);
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error, true);
        } finally { }
    };


    const resetState = () => {
        loadingState.stopLoading();
        loadingState.stopViewModelLoading();
    }

    return {
        viewModelLoading: loadingState.viewModelLoading,
        isLoading: loadingState.isLoading,
        errorLoading: loadingState.errorLoading,
        setTempAddress,
        loadActor,
        topUpWallet,
        resetState,
    };
});
